import joinUrl from 'url-join';
import { replacerWithoutPattern } from './utils';

export function replaceGdprMacros(url: string, macros: AnyObject = {}): string {
  let templatedUrl = url;
  templatedUrl = replacerWithoutPattern(
    templatedUrl,
    /((#|\$){|\[|%5B)(gdpr|is_gdpr)(}|\]|%5D)/gi,
    macros.gdpr
  );
  templatedUrl = replacerWithoutPattern(
    templatedUrl,
    /((#|\$){|\[|%5B)(tcfv2|gdpr_consent_string|gdpr_consent)(}|\]|%5D)/gi,
    macros.tcfv2
  );
  templatedUrl = replacerWithoutPattern(
    templatedUrl,
    /((#|\$){|\[|%5B)gdpr_consent_[0-9]+(}|\]|%5D)/gi,
    macros.tcfv2
  );

  const regexPersonalMacro = /((#|$){|\[|%5B)+(aaid|user_agent|user_advertising_id|ip)(]|})+/gi;
  templatedUrl = replacerWithoutPattern(
    templatedUrl,
    /((#|\$){|\[|%5B)gdpr_pd(}|\]|%5D)/gi,
    templatedUrl.match(regexPersonalMacro) ? '1' : '0'
  );
  return templatedUrl;
}

export function templateMacros(
  url: string = '',
  macros: AnyObject = {}
): string {
  let templatedUrl = url.slice();

  templatedUrl = replaceGdprMacros(templatedUrl, macros);
  Object.keys(macros).forEach(macro => {
    templatedUrl = replacerWithoutPattern(
      templatedUrl,
      new RegExp(`\\[${macro}\\]`, 'gi'),
      encodeURIComponent(macros[macro])
    );
  });

  return templatedUrl;
}

export function updateQueryStringParameters(
  url: string,
  params: AnyObject
): string {
  if (!url || !params) {
    return '';
  }

  let hash;
  [url, hash] = url.split('#');
  hash = hash ? '#' + hash : '';

  try {
    const parsedUrl = new window.URL(url);
    const queryParameters = new window.URLSearchParams(parsedUrl.search);

    Object.keys(params).map(key => {
      if (typeof params[key] !== 'boolean' && !params[key]) {
        return;
      }

      queryParameters.set(key, params[key]);
    });

    const queryParametersString = queryParameters
      .toString()
      .split('&')
      .sort((a, b) => {
        const regex = new RegExp(/force_redirect/);
        if (regex.test(a)) {
          return +1;
        } else if (regex.test(b)) {
          return -1;
        }
        return 0;
      })
      .join('&');

    return joinUrl(
      parsedUrl.origin,
      parsedUrl.pathname,
      '?' + queryParametersString + hash
    );
  } catch {
    if (window.isOgyPreview && params.force_redirect) {
      return params.force_redirect;
    }
    return url + hash;
  }
}

export function isValidUrl(url: string): boolean {
  const urlRegex = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/.*/;
  return Boolean(url) && urlRegex.test(url);
}

export function isSpecialUrlScheme(url: string): boolean {
  const urlRegex = /^(mailto|tel):[^\/]{2}.+/;
  return Boolean(url) && urlRegex.test(url);
}

/**
 * True when `url` parses as a syntactically valid absolute URL with the
 * `http:` or `https:` scheme — i.e. an URL the browser can navigate to as a
 * web destination. Returns false for deep-links (`app://`, `intent://`), data
 * / `javascript:` / `mailto:` schemes, and any malformed input.
 *
 * Stricter than `isValidUrl` (which accepts any `<scheme>://...` shape).
 */
export function isNavigableHttpUrl(url: string): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function generateUrlWithRegion(
  endPoint?: string,
  region?: string
): string {
  if (!endPoint) {
    return '';
  }

  if (!region) {
    return endPoint;
  }

  const segmentUrl = endPoint.split('.', 1);
  return endPoint.replace(segmentUrl[0], `${segmentUrl[0]}-${region}`);
}

/**
 * Fix SL3-3712
 * Android decode the url when it's open the webview, so we need to re-encode the url (only params) if needed
 */
export function sanitizeRedirectURL(url: string, replaceRegex: RegExp): string {
  const sanitizedURL = new URL(url);

  // if replaceRegex is in search params, check if the url is encoded, if not encode the url
  sanitizedURL.search = sanitizedURL.search.replace(replaceRegex, match => {
    if (match.indexOf('&') > 0) {
      return encodeURIComponent(match);
    }
    return match;
  });

  return sanitizedURL.toString();
}
